// src/generators/dzial9_prawdopodobienstwo/temat1_kombinatoryka/podtemat1_podstawy_types/DigitOnceBelowH00Task.js
// Zadanie (Podstawy): Ile jest liczb trzycyfrowych mniejszych od H00,
// które w swoim zapisie mają dokładnie jedną cyfrę d?
// Rozwiązanie: czysta "suma iloczynów" (przypadki rozłączne + zasada mnożenia).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

function randInt(a, b) {
  return Math.floor(Math.random() * (b - a + 1)) + a;
}

function computeAnswer(H, d) {
  // I = 1, gdy d może stać w setkach (czyli d <= H-1), wpp 0
  const I = d <= H - 1 ? 1 : 0;
  // setki, gdy d jest w dziesiątkach/jednościach: 1..H-1, ale ≠ d jeśli d <= H-1
  const hundreds = (H - 1) - I;

  // Przypadki rozłączne:
  // (1) d w setkach: 9 * 9 (dziesiątki i jedności != d) — tylko jeśli d <= H-1
  // (2) d w dziesiątkach: hundreds * 9
  // (3) d w jedności:     hundreds * 9
  return I * 81 + 2 * hundreds * 9;
}

export function generateTask() {
  const H = randInt(4, 9); // liczymy liczby < H00, H ∈ {4..9}
  const d = randInt(1, 9); // cyfra, która ma wystąpić dokładnie raz (bez 0 dla prostoty)
  const answer = computeAnswer(H, d);
  return {
    type: "DigitOnceBelowH00Task",
    data: { H, d, answer },
  };
}

export function renderTask({ data }) {
  const { H, d } = data;
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        Ile jest liczb trzycyfrowych mniejszych od{" "}
        <InlineMath math={`${H}00`} />, które w swoim zapisie mają{" "}
        <strong>dokładnie jedną</strong> cyfrę <InlineMath math={`${d}`} />?
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=""
      placeholder="Wpisz liczbę"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") {
    return { correct: false, message: "Podaj liczbę." };
  }
  const given = parseInt(userAnswer.raw, 10);
  if (Number.isNaN(given)) {
    return { correct: false, message: "Podaj liczbę całkowitą." };
  }
  const correct = given === data.answer;
  userAnswer.correctAnswer = data.answer;
  return { correct, message: correct ? "Dobrze!" : "Spróbuj ponownie." };
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.answer}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`${userAnswer.raw}`} />;
}

export function renderExplanation({ data }) {
  const { H, d, answer } = data;

  // I = 1, gdy d może stać w setkach (czyli d <= H-1), wpp 0
  const I = d <= H - 1 ? 1 : 0;
  // ile opcji na setki, gdy d nie stoi w setkach
  const hundreds = (H - 1) - I;

  // Składowe sumy (dla przejrzystości w objaśnieniach)
  const case1 = I * 81;         // d w setkach: 9·9
  const case2 = hundreds * 9;   // d w dziesiątkach
  const case3 = hundreds * 9;   // d w jedności

  // Poprawny LaTeX dla zbioru {1,2,...,H-1}
  const setHundredsLatex = `\\{1,2,\\ldots, ${H - 1}\\}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      {/* Krok 1 */}
      <p><strong>Krok 1. Ustal możliwe cyfry setek.</strong></p>
      <p>
        Warunek <InlineMath math={`<\\, ${H}00`} /> oznacza, że cyfra setek pochodzi ze zbioru{" "}
        <InlineMath math={setHundredsLatex} /> (czyli jest ich{" "}
        <InlineMath math={`${H - 1}`} />).
      </p>

      {/* Krok 2 */}
      <p><strong>Krok 2. Rozbij na przypadki rozłączne (gdzie jest cyfra <InlineMath math={`${d}`} />?).</strong></p>
      <ol>
        <li><em>Przypadek 1:</em> <InlineMath math={`${d}`} /> w setkach (możliwe tylko, gdy <InlineMath math={`${d} \\le ${H - 1}`} />).</li>
        <li><em>Przypadek 2:</em> <InlineMath math={`${d}`} /> w dziesiątkach.</li>
        <li><em>Przypadek 3:</em> <InlineMath math={`${d}`} /> w jedności.</li>
      </ol>

      {/* Krok 3 */}
      <p><strong>Krok 3. Policz możliwości w każdym przypadku (zasada mnożenia).</strong></p>
      <ul>
        <li>
          <em>Przypadek 1 (setki = <InlineMath math={`${d}`} />):</em>{" "}
          dziesiątki: <InlineMath math={'9'} /> opcji (dowolna cyfra oprócz <InlineMath math={`${d}`} />),{" "}
          jedności: <InlineMath math={'9'} /> opcji ⇒ <InlineMath math={'9\\cdot 9 = 81'} />.
          Gdy <InlineMath math={`${d} > ${H - 1}`} />, ten przypadek jest niemożliwy (liczymy <InlineMath math={'0'} />).
          <BlockMath math={`${I} \\cdot 81 = ${case1}`} />
        </li>
        <li>
          <em>Przypadek 2 (dziesiątki = <InlineMath math={`${d}`} />):</em>{" "}
          setki: <InlineMath math={`${hundreds}`} /> możliwości (nie mogą być równe{" "}
          <InlineMath math={`${d}`} /> jeśli <InlineMath math={`${d} \\le ${H - 1}`} />),{" "}
          jedności: <InlineMath math={'9'} /> ⇒
          <BlockMath math={`${hundreds} \\cdot 9 = ${case2}`} />
        </li>
        <li>
          <em>Przypadek 3 (jedności = <InlineMath math={`${d}`} />):</em>{" "}
          analogicznie ⇒ <BlockMath math={`${hundreds} \\cdot 9 = ${case3}`} />
        </li>
      </ul>

      {/* Krok 4 */}
      <p><strong>Krok 4. Zastosuj zasadę dodawania (zsumuj przypadki).</strong></p>
      <BlockMath math={`${case1} \\; + \\; ${case2} \\; + \\; ${case3} \\;=\\; ${answer}`} />
    </div>
  );
}
